import React, { useState, useCallback, FormEvent } from 'react';
import { isValidEmail } from '../utils/validation';
import './EmailLoginForm.css';

interface EmailLoginFormProps {
  onSubmit: (email: string, password: string, rememberMe: boolean) => Promise<void>;
  onBackToMenu: () => void;
  onForgotPassword: () => void;
  onSwitchToSignup: () => void;
  isLoading?: boolean;
}

interface FormErrors {
  email?: string;
  password?: string;
}

/**
 * EmailLoginForm Component
 * Form for logging in with email and password
 */
export const EmailLoginForm: React.FC<EmailLoginFormProps> = ({
  onSubmit,
  onBackToMenu,
  onForgotPassword,
  onSwitchToSignup,
  isLoading = false
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true); // Checked by default
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Validate individual fields
  const validateField = useCallback((field: string, value: string): string | undefined => {
    switch (field) {
      case 'email':
        if (!value) return 'Email is required';
        if (!isValidEmail(value)) return 'Please enter a valid email address';
        return undefined;
      
      case 'password':
        if (!value) return 'Password is required';
        return undefined;
      
      default:
        return undefined;
    }
  }, []);

  // Handle field blur
  const handleBlur = useCallback((field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    
    const value = field === 'email' ? email : password;
    const error = validateField(field, value);
    setErrors(prev => ({ ...prev, [field]: error }));
  }, [email, password, validateField]);

  // Validate all fields
  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {
      email: validateField('email', email),
      password: validateField('password', password)
    };

    setErrors(newErrors);
    setTouched({
      email: true,
      password: true
    });

    return !Object.values(newErrors).some(error => error !== undefined);
  }, [email, password, validateField]);

  // Handle form submission
  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(email, password, rememberMe);
    } catch (error) {
      console.error('Login failed:', error);
    }
  }, [email, password, rememberMe, validateForm, onSubmit]);

  return (
    <div className="email-login-form">
      <button
        className="email-login-back"
        onClick={onBackToMenu}
        type="button"
        disabled={isLoading}
      >
        ← Back to Menu
      </button>

      <h2 className="email-login-title">Welcome Back!</h2>

      <form onSubmit={handleSubmit} noValidate>
        {/* Email Field */}
        <div className="email-login-field">
          <label htmlFor="login-email" className="email-login-label">
            Email Address
          </label>
          <input
            id="login-email"
            type="email"
            className={`email-login-input ${
              touched.email && errors.email ? 'email-login-input-error' : ''
            }`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => handleBlur('email')}
            placeholder="your.email@example.com"
            disabled={isLoading}
            required
            autoComplete="email"
            autoFocus
          />
          {touched.email && errors.email && (
            <span className="email-login-error">{errors.email}</span>
          )}
        </div>

        {/* Password Field */}
        <div className="email-login-field">
          <label htmlFor="login-password" className="email-login-label">
            Password
          </label>
          <div className="email-login-input-wrapper">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              className={`email-login-input ${
                touched.password && errors.password ? 'email-login-input-error' : ''
              }`}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => handleBlur('password')}
              placeholder="Enter password"
              disabled={isLoading}
              required
              autoComplete="current-password"
            />
            <button
              type="button"
              className="email-login-toggle-password"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              disabled={isLoading}
            >
              {showPassword ? '👁' : '👁‍🗨'}
            </button>
          </div>
          {touched.password && errors.password && (
            <span className="email-login-error">{errors.password}</span>
          )}
        </div>

        {/* Remember Me & Forgot Password */}
        <div className="email-login-options">
          <label className="email-login-checkbox-label">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              disabled={isLoading}
            />
            <span>Remember me</span>
          </label>

          <button
            type="button"
            className="email-login-forgot-link"
            onClick={onForgotPassword}
            disabled={isLoading}
          >
            Forgot password?
          </button>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="email-login-submit"
          disabled={isLoading}
        >
          {isLoading ? '⟳ Logging in...' : 'Login'}
        </button>

        {/* Switch to Signup */}
        <div className="email-login-footer">
          Don't have an account?{' '}
          <button
            type="button"
            className="email-login-link"
            onClick={onSwitchToSignup}
            disabled={isLoading}
          >
            Sign up
          </button>
        </div>
      </form>
    </div>
  );
};

export default EmailLoginForm;
